<template>
  <div v-if="task_id">
    <div class='sl_form_title'>
      <h3>Comments</h3>
    </div>
    <v-divider class='my-2'></v-divider>
    <div class="sl_comments__body">
      <div class="sl_comments__body--reply" v-if="form.comment_id">
        <comment-reply
            :has_cancel="true" @cancel="form.comment_id = false"
            :comment="form.comment"
        ></comment-reply>
      </div>
      <div class="sl_comments__body--area">
        <div class="sl_comments__body--area__user">
          <v-btn color="primary" class="ml-0" icon small>{{ user | getText('short_name', 'N') }}</v-btn>
        </div>
        <div class="sl_comments__body--area__input">
          <markdown
              id="comment-input"
              ref="comment-input"
              label="Your comment"
              v-model="form.text"
              :editing="true"
              fieldType="input"
              :errors="errors && errors.text ? errors.text : []"
          />
        </div>
        <div class="sl_comments__body--area__btn">
          <v-btn small icon color="info" class="mr-0" @click="save(form)"><v-icon size="16">send</v-icon></v-btn>
        </div>
      </div>
    </div>

    <comment-item
        class="mt-4"
        v-for="comment in list.items"
        :comment="comment"
        :id="comment.id"
        @save="item => save(item)"
        @remove="item => remove(item)"
        @reply="item => reply(item)"
        @requestScrollTo="scrollTo"
    ></comment-item>

    <div class="sl_comments__loading">
      <v-progress-circular
          v-if="loading"
          indeterminate
          color="success"
          :width="2"
          :size="50"
      ></v-progress-circular>
      <v-btn
          small
          v-if="list.pagination.page < list.pagination.last_page"
          @click="loadMoreComments"
          color="info"
      >
        Load more comments
        <v-icon>keyboard_arrow_down</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
  import * as mixins from '@/mixins'
  import reply from '@/components/tasks/comments/reply'
  import item from '@/components/tasks/comments/item'
  import markdown from '@/components/markdown'
  import { mapActions } from 'vuex'

  export default {
    name: 'TaskComments',
    props: ['task_id', 'isLoading'],
    mixins: [mixins.removable, mixins.filter, mixins.sort, mixins.errors, mixins.preloader, mixins.user],
    data() {
      return {
        api: this.$api.comments,
        list: {
          pagination: {
            page: 1,
            rowsPerPage: 10,
            totalItems: 0,
            current_page: 0,
            per_page: 0,
            last_page: 0,
          },
          items: [],
        },
        filter: {
          task_id: this.task_id,
        },
        loading: false,
        sort: {
          order: 'created_at',
          direction: 'desc'
        },
        form: {
          text: '',
        },
        deletedItem: null,
        isCommentsFetched: false,
      }
    },
    methods: {
      ...mapActions(['queue']),
      scrollTo(id, params) {
        params = {
          ...params,
          behavior: 'smooth',
        }
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView(params)

          element.classList.add('sl_comments__highlighted');

          setTimeout(() => {
            if (this.$route.query.focusedCommentId !== id) {
              element.classList.remove('sl_comments__highlighted');
            }
          }, 700);
        }
      },
      checkFocusedComment() {
        if (this.$route.query && this.$route.query.focusedCommentId) {
          this.$nextTick(() => {
            this.scrollTo(this.$route.query.focusedCommentId);
          })
        }
      },
      save(comment) {
        this.$refs['comment-input'].hideEditor()

        this.api.save({...comment, task_id: this.task_id}).then(() => {
          this.form = {text: ""}
          this.list.pagination.page = 1
          this.list.items = []
          this.load()
        }, response => {
          this.errors = response.errors
        })
      },
      reply(comment) {
        this.form = {
          ...this.form,
          comment: comment,
          comment_id: comment.id
        }
      },
      getData(params) {
        this.loading = true
        return this.getList(params).then((response) => {
          const data = response.data.data || response.data;

          this.list.items = this.getUniqueById([...this.list.items, ...this.onLoadTransform(data)]);
          if (this.deletedItem) {
            this.list.items = this.list.items.filter((item) => item.id !== this.deletedItem.id);
            this.deletedItem = null;
          }
          this.list.pagination.totalItems = response.data.total ? response.data.total : response.data.length
          this.list.pagination.current_page = response.data.current_page
          this.list.pagination.per_page = response.data.per_page
          this.list.pagination.last_page = response.data.last_page

          this.loading = false
          this.isCommentsFetched = true
        })
      },
      getUniqueById(arrays) {
        if (!arrays) return [];
        const combinedArray = [...arrays];
        const seenIds = new Set();

        return combinedArray.filter(item => {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            return true;
          }
          return false;
        });
      },
      load() {
        this.queue(this.getData(this.collectParams()))
      },
      loadMoreComments() {
        this.list.pagination.page++
        this.load()
      },
      onLoadTransform(data) {
        return data.map(item => ({...item, editing: false}))
      },
      getList(params) {
        return this.api.list(params)
      },
      collectParams() {
        let params = {
          page: this.list.pagination.page,
          limit: 100
        }

        if (typeof this.collectSort === "function") {
          params = this.collectSort(params)
        }

        if (typeof this.collectFilter === "function") {
          params = this.collectFilter(params)
        }

        return params
      }
    },
    watch: {
      'task_id'() {
        this.filter.task_id = this.task_id
      },
      'list.items'() {
        if (this.isCommentsFetched && !this.isLoading) {
          this.checkFocusedComment();
        }
      },
    },
    mounted() {
      this.load();
    },
    components: {
      'comment-item': item,
      'comment-reply': reply,
      'markdown': markdown,
    }
  }
</script>
